import { useCallback, useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { FORCE_UPDATE_AFTER_MS, CHECK_INTERVAL_MS } from '@/constants/pwa';
import { toastQueue } from '@/main';

export function useSWUpdatePrompt(): void {
  const { needRefresh: [needRefresh, setNeedRefresh], updateServiceWorker } = useRegisterSW();

  const update = useCallback(() => {
    localStorage.removeItem('pwa-force-update-timeout');
    setNeedRefresh(false);
    updateServiceWorker();
  }, [updateServiceWorker, setNeedRefresh]);

  const checkForcedUpdateStatus = useCallback(() => {
    const timeout = localStorage.getItem('pwa-force-update-timeout');
    if (timeout && Date.now() - parseInt(timeout) > FORCE_UPDATE_AFTER_MS) {
      return true;
    }
    return false;
  }, []);

  const promptUpdate = useCallback(() => {
    const now = Date.now();

    if (!localStorage.getItem('pwa-force-update-timeout')) {
      localStorage.setItem('pwa-force-update-timeout', now.toString());
    }

    toastQueue.add(
      {
        message: 'New content available.',
        actionProps: {
          children: 'Update',
          onPress: update,
        },
        variant: 'info',
      },
    );
  }, [update]);

  useEffect(() => {
    if (!needRefresh) {
      if (localStorage.getItem('pwa-force-update-timeout')) {
        localStorage.removeItem('pwa-force-update-timeout');
      }
      return;
    }

    if (!localStorage.getItem('pwa-force-update-timeout')) {
      localStorage.setItem('pwa-force-update-timeout', Date.now().toString());
      promptUpdate();
    }

    if (checkForcedUpdateStatus()) {
      update();
      return;
    }

    const interval = setInterval(() => checkForcedUpdateStatus() ? update() : promptUpdate(), CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [needRefresh, update, checkForcedUpdateStatus, promptUpdate]);
}
